/**
 * Клиент API.
 *
 * Сессия живёт в httpOnly-куке, поэтому токены во фронтенде не хранятся
 * и в localStorage ничего не кладётся. credentials: 'include' обязателен.
 */

import type {
  AccessLogEntry,
  BatchReport,
  IngestAttempt,
  BranchRule,
  DictionaryCatalog,
  IngestProgress,
  StreamTotals,
  IngestToken,
  BranchSummary,
  RulePreview,
  WarehouseKindRow,
  BranchConflict,
  CompareMode,
  AccessMatrix,
  AdminRole,
  AdminUser,
  CurrentUser,
  DashboardData,
  DimensionValue,
  ReportDescriptor,
  ReportResult,
  ScopeDimension,
  ScopeRule,
  EffectiveAccess,
  PermissionAuditEntry,
  SectionCatalog,
  GrossProfitCheck,
} from './types'

// ---------------------------------------------------------------------------
// Просмотр «как пользователь»
// ---------------------------------------------------------------------------
//
// Администратор включает просмотр на экране прав сотрудника. Пока он включён,
// запросы чтения отчётов уходят с ?as_user=<id>: сервер сам подменяет права
// и пишет в журнал, кто на самом деле смотрел. Хранится в sessionStorage —
// просмотр живёт до закрытия вкладки и не переживает выход из системы.

const PREVIEW_KEY = 'svodka-preview-as'

function readPreview(): string | null {
  try {
    return sessionStorage.getItem(PREVIEW_KEY)
  } catch {
    return null
  }
}

let previewAs: string | null = readPreview()

/**
 * Смена того, под чьими правами идут запросы: вход, выход, начало и конец
 * просмотра «как пользователь». Модули с памятью в пределах страницы
 * (кеш отчётов, последние показанные числа, свежесть) подписываются
 * и чистятся: иначе следующий вошедший в этой вкладке на долю секунды
 * увидел бы цифры прошлого — и как они «докручиваются» к его собственным.
 */
const sessionListeners = new Set<() => void>()

export function onSessionChange(listener: () => void): () => void {
  sessionListeners.add(listener)
  return () => {
    sessionListeners.delete(listener)
  }
}

function changeSession(): void {
  sessionEpoch += 1
  for (const listener of sessionListeners) listener()
}

/**
 * Сессия кончилась на сервере (срок вышел, пароль сменили, пользователя
 * заблокировали). Без этого страница оставалась открытой, каждый запрос
 * получал 401, а человек видел «данные устарели» и не понимал, что нужно
 * просто войти снова.
 */
const expiryListeners = new Set<() => void>()

export function onSessionExpired(listener: () => void): () => void {
  expiryListeners.add(listener)
  return () => {
    expiryListeners.delete(listener)
  }
}

/** Пути, где 401 — обычный ответ, а не потеря сессии. */
const AUTH_PATHS = ['/api/auth/login', '/api/auth/me', '/api/auth/context']

function sessionExpired(): void {
  stopPreview()
  changeSession()
  for (const listener of expiryListeners) listener()
}

export function startPreview(userId: string): void {
  previewAs = userId
  changeSession()
  try {
    sessionStorage.setItem(PREVIEW_KEY, userId)
  } catch {
    // Без хранилища просмотр продержится до перезагрузки страницы.
  }
}

/**
 * Под чьими правами сейчас идут запросы — для ключей кэша.
 *
 * Кэш отчётов живёт в памяти страницы. Без этого после выхода и входа
 * другого человека в той же вкладке или после выхода из просмотра
 * «как пользователь» до пяти минут показывались бы чужие цифры.
 */
let sessionEpoch = 0

export function cacheScope(): string {
  return `${sessionEpoch}:${previewAs ?? ''}`
}

export function isPreviewing(): boolean {
  return previewAs !== null
}

export function stopPreview(): void {
  const was = previewAs
  previewAs = null
  if (was !== null) changeSession()
  try {
    sessionStorage.removeItem(PREVIEW_KEY)
  } catch {
    // нечего чистить
  }
}

/** Маршруты, которые сервер умеет отдавать от имени сотрудника. */
const PREVIEW_PATHS = ['/api/auth/me', '/api/reports', '/api/dimensions/', '/api/dashboard', '/api/ingest', '/api/etl']

function withPreview(path: string): string {
  if (!previewAs || path.includes('as_user=')) return path
  if (!PREVIEW_PATHS.some((prefix) => path.startsWith(prefix))) return path
  return `${path}${path.includes('?') ? '&' : '?'}as_user=${encodeURIComponent(previewAs)}`
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
    readonly details?: unknown,
  ) {
    super(message)
  }

  /** Отказ по правам — интерфейс показывает его иначе, чем поломку. */
  get isForbidden(): boolean {
    return this.status === 403
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(withPreview(path), {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  const text = await response.text()
  const payload = text ? (JSON.parse(text) as Record<string, unknown>) : {}

  if (response.status === 401 && !AUTH_PATHS.some((prefix) => path.startsWith(prefix))) {
    sessionExpired()
  }

  if (!response.ok) {
    throw new ApiError(
      (payload.error as string) ?? `Ошибка ${response.status}`,
      response.status,
      payload.code as string | undefined,
      payload.details,
    )
  }

  return payload as T
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

const put = <T>(path: string, body: unknown): Promise<T> =>
  request<T>(path, { method: 'PUT', body: JSON.stringify(body) })

export const api = {
  // --- Аутентификация ---
  // Вход и выход завершают просмотр: иначе следующий вошедший в этой вкладке
  // получил бы чужой as_user на каждом запросе — 403 у сотрудника,
  // чужие цифры под своим меню у администратора.
  login: (email: string, password: string) => {
    stopPreview()
    changeSession()
    return post<{ user: CurrentUser }>('/api/auth/login', { email, password })
  },
  logout: () => {
    stopPreview()
    changeSession()
    return post<{ status: string }>('/api/auth/logout')
  },
  me: () => request<{ user: CurrentUser }>('/api/auth/me'),

  // --- Отчёты ---
  dashboard: () => request<DashboardData>('/api/dashboard'),

  branchConflicts: () => request<{ conflicts: BranchConflict[] }>('/api/etl/quality/branch-conflicts'),

  ingestProgress: (databaseId?: string) =>
    request<IngestProgress>(
      `/api/ingest/progress${databaseId ? `?database_id=${encodeURIComponent(databaseId)}` : ''}`,
    ),

  ingestTotals: (stream: string, databaseId?: string) =>
    request<StreamTotals>(
      `/api/ingest/totals?stream=${stream}` +
        `${databaseId ? `&database_id=${encodeURIComponent(databaseId)}` : ''}`,
    ),

  ingestDictionaries: () => request<{ catalogs: DictionaryCatalog[] }>('/api/ingest/dictionaries'),

  etlBatches: (limit = 20, databaseId?: string, problemsOnly = false) =>
    request<{ batches: BatchReport[] }>(
      `/api/etl/batches?limit=${limit}` +
        `${databaseId ? `&database_id=${encodeURIComponent(databaseId)}` : ''}` +
        `${problemsOnly ? '&problems=true' : ''}`,
    ),
  ingestAttempts: (databaseId?: string) =>
    request<{ attempts: IngestAttempt[] }>(
      `/api/ingest/attempts${databaseId ? `?database_id=${encodeURIComponent(databaseId)}` : ''}`,
    ),
  reports: () => request<{ reports: ReportDescriptor[] }>('/api/reports'),
  report: (code: string) => request<ReportDescriptor>(`/api/reports/${code}`),

  runReport: (
    code: string,
    body: {
      period?: { from: string; to: string }
      filters?: Record<string, string[]>
      group_by?: string[]
      metrics?: string[]
      order_by?: { key: string; direction: 'asc' | 'desc' }
      limit?: number
      compare?: CompareMode
    },
    asUser?: string | null,
  ) => post<ReportResult>(`/api/reports/${code}/run${asUser ? `?as_user=${encodeURIComponent(asUser)}` : ''}`, body),

  /**
   * Выгрузка. Браузер сам сохраняет файл по ссылке из Content-Disposition,
   * поэтому ответ обрабатывается как blob, а не как JSON.
   */
  exportReport: async (
    code: string,
    body: Record<string, unknown>,
    format: 'xlsx' | 'csv',
    /** Предпросмотр прав из отчёта («Смотреть от имени»): выгрузка — тем же срезом, что на экране. */
    asUser?: string | null,
  ): Promise<void> => {
    const path = `/api/reports/${code}/export?format=${format}${asUser ? `&as_user=${encodeURIComponent(asUser)}` : ''}`
    const response = await fetch(withPreview(path), {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })

    if (!response.ok) {
      const payload = await response.json().catch(() => ({}))
      throw new ApiError(payload.error ?? 'Не удалось выгрузить', response.status, payload.code)
    }

    const disposition = response.headers.get('Content-Disposition') ?? ''
    const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1]
    const fileName = encoded ? decodeURIComponent(encoded) : `${code}.${format}`

    const url = URL.createObjectURL(await response.blob())
    const link = document.createElement('a')
    link.href = url
    link.download = fileName
    document.body.append(link)
    link.click()
    link.remove()
    URL.revokeObjectURL(url)
  },

  uploadGrossProfit: async (
    file: File,
    options: { periodLabel?: string; month?: string; sourceDatabaseId?: string } = {},
  ) => {
    const form = new FormData()
    form.append('file', file)

    const query = new URLSearchParams()
    if (options.periodLabel) query.set('period_label', options.periodLabel)
    if (options.month) query.set('month', options.month)
    if (options.sourceDatabaseId) query.set('source_database_id', options.sourceDatabaseId)
    const suffix = query.toString()

    const response = await fetch(`/api/data/gross-profit${suffix ? `?${suffix}` : ''}`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    })

    const payload = await response.json().catch(() => ({}))
    if (!response.ok) {
      throw new ApiError(payload.error ?? 'Не удалось загрузить файл', response.status, payload.code, payload.details)
    }
    return payload as {
      file_name: string
      batch_id: string
      rows_loaded: number
      lines_without_cost: number
      total_row_found: boolean
      totals: { declared: Record<string, string | null>; computed: Record<string, string>; matched: boolean }
      source: string
      reconciliation: GrossProfitCheck | null
    }
  },

  reconcileGrossProfit: (batchId: string, body: { month: string; source_database_id?: string }) =>
    post<{ reconciliation: GrossProfitCheck }>(`/api/data/gross-profit/${encodeURIComponent(batchId)}/reconcile`, body),

  grossProfitChecks: (limit = 10) =>
    request<{ checks: GrossProfitCheck[] }>(`/api/ingest/gross-profit-checks?limit=${limit}`),

  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ status: string; other_sessions_revoked: number }>('/api/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    }),

  dimensionValues: (code: string, search?: string) =>
    request<{ values: DimensionValue[] }>(
      `/api/dimensions/${code}/values${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ),

  // --- Админка ---
  adminUsers: () => request<{ users: AdminUser[] }>('/api/admin/users'),
  adminRoles: () => request<{ roles: AdminRole[] }>('/api/admin/roles'),
  accessMatrix: () => request<AccessMatrix>('/api/admin/access-matrix'),

  setReportAccess: (body: {
    subject_type: 'role' | 'user'
    subject_id: string
    report_code: string
    mode: 'allow' | 'deny' | null
    allowed_metrics?: string[] | null
  }) => put<{ status: string }>('/api/admin/report-access', body),

  createUser: (body: {
    email: string
    full_name: string
    position?: string
    is_admin?: boolean
    can_monitor_ingest?: boolean
    role_ids?: number[]
  }) => post<{ id: string; generated_password: string | null }>('/api/admin/users', body),

  setUserRoles: (userId: string, roleIds: number[]) =>
    put<{ status: string }>(`/api/admin/users/${userId}/roles`, { role_ids: roleIds }),

  blockUser: (userId: string, reason: string) =>
    post<{ sessionsRevoked: number }>(`/api/admin/users/${userId}/block`, { reason }),

  unblockUser: (userId: string) => post<{ status: string }>(`/api/admin/users/${userId}/unblock`),

  resetPassword: (userId: string) => post<{ password: string }>(`/api/admin/users/${userId}/reset-password`),

  revokeSessions: (userId: string) => post<{ revoked: number }>(`/api/admin/users/${userId}/revoke-sessions`),

  scopeDimensions: () => request<{ dimensions: ScopeDimension[] }>('/api/admin/scope-dimensions'),

  adminDimensionValues: (dimension: string, search?: string) =>
    request<{ values: DimensionValue[] }>(
      `/api/admin/dimension-values?dimension=${dimension}${search ? `&search=${encodeURIComponent(search)}` : ''}`,
    ),

  scopeRules: (subjectType: 'role' | 'user', subjectId: string) =>
    request<{ rules: ScopeRule[] }>(
      `/api/admin/scope-rules?subject_type=${subjectType}&subject_id=${subjectId}`,
    ),

  createScopeRule: (body: {
    subject_type: 'role' | 'user'
    subject_id: string
    dimension: string
    mode: 'allow' | 'deny'
    values?: string[]
    period_mode?: 'rolling_window' | 'not_before' | 'closed_only'
    period_days?: number
    period_from?: string
    comment?: string
  }) => post<{ id: string }>('/api/admin/scope-rules', body),

  deleteScopeRule: (id: string) => request<{ status: string }>(`/api/admin/scope-rules/${id}`, { method: 'DELETE' }),

  branchRules: () =>
    request<{ rules: BranchRule[]; branches: BranchSummary[]; warehouses?: WarehouseKindRow[] }>('/api/admin/branch-rules'),

  branchRulesPreview: () => request<RulePreview>('/api/admin/branch-rules/preview'),

  createBranchRule: (body: {
    target: string
    applies_to: string
    priority: number
    match_on: string
    pattern: string
    value: string
    note?: string
  }) => post<{ id: number }>('/api/admin/branch-rules', body),

  deleteBranchRule: (id: number) =>
    request<{ status: string }>(`/api/admin/branch-rules/${id}`, { method: 'DELETE' }),

  applyBranchRules: () =>
    post<{ departments: number; warehouses: number; without_branch: number }>(
      '/api/admin/branch-rules/apply',
      {},
    ),

  createBranch: (body: { code: string; name: string }) => post<{ id: number }>('/api/admin/branches', body),

  ingestTokens: () => request<{ tokens: IngestToken[] }>('/api/admin/ingest-tokens'),

  issueIngestToken: (body: { name: string; database_id?: string; streams?: string[] }) =>
    post<{ id: number; token: string; prefix: string }>('/api/admin/ingest-tokens', body),

  revokeIngestToken: (id: number, reason: string) =>
    post<{ status: string }>(`/api/admin/ingest-tokens/${id}/revoke`, { reason }),

  accessLog: (limit = 200) => request<{ entries: AccessLogEntry[] }>(`/api/admin/access-log?limit=${limit}`),

  permissionAudit: (limit = 200) =>
    request<{ entries: PermissionAuditEntry[] }>(`/api/admin/permission-audit?limit=${limit}`),

  userHistory: (userId: string) => request<{ entries: PermissionAuditEntry[] }>(`/api/admin/users/${userId}/history`),

  effectiveAccess: (userId: string) => request<EffectiveAccess>(`/api/admin/users/${userId}/effective`),

  setAdmin: (userId: string, isAdmin: boolean) =>
    put<{ status: string }>(`/api/admin/users/${userId}/admin`, { is_admin: isAdmin }),

  setIngestMonitor: (userId: string, allowed: boolean) =>
    put<{ status: string }>(`/api/admin/users/${userId}/ingest-monitor`, { allowed }),

  sectionCatalog: () => request<SectionCatalog>('/api/admin/sections'),

  hidden: (subjectType: 'role' | 'user', subjectId: string) =>
    request<{ metrics: Record<string, string[]>; sections: string[] }>(
      `/api/admin/hidden?subject_type=${subjectType}&subject_id=${subjectId}`,
    ),

  setMetricHidden: (body: { subject_type: 'role' | 'user'; subject_id: string; report_code: string; metric: string; hidden: boolean }) =>
    put<{ status: string }>('/api/admin/metric-hidden', body),

  setSectionHidden: (body: { subject_type: 'role' | 'user'; subject_id: string; section_code: string; hidden: boolean }) =>
    put<{ status: string }>('/api/admin/section-hidden', body),
}

// ---------------------------------------------------------------------------
// Финансовая разметка (src/http/routes-finance.ts)
// ---------------------------------------------------------------------------

export type IntercompanyMode = 'exclude' | 'include'
export interface FinanceSettings {
  debts_intercompany: IntercompanyMode
}

type Amount = number | string | null

export interface CashflowItemClassRow {
  guid: string
  name: string
  direction_1c: string | null
  cf_section: string
  flow: string
  pnl_line: string | null
  source: 'auto' | 'manual'
  suggested: { cf_section: string; flow: string; pnl_line: string | null }
  note: string | null
  updated_by: string | null
  operations: number
  inflow: Amount
  outflow: Amount
}

export interface ExpenseItemClassRow {
  guid: string
  name: string
  pnl_line: string
  source: 'auto' | 'manual'
  suggested: string
  note: string | null
  updated_by: string | null
  amount_12m: Amount
  amount_total: Amount
  lines: number
}

export interface OwnCounterpartyRow {
  guid: string
  name: string
  inn: string | null
  is_own: boolean
  source: 'manual' | 'inn' | 'none'
  inn_match: boolean
  organization: string | null
  note: string | null
  updated_by: string | null
  receivable: Amount
  payable: Amount
}

export interface DebtBranchCoverageRow {
  kind: string
  relation: string
  branch_source: string
  rows: number
  amount: Amount
}

export interface FinanceOptions {
  cf_sections: string[]
  cf_flows: string[]
  cashflow_pnl_lines: string[]
  expense_lines: string[]
}

type Window = { from: string | null; to: string | null }

export const financeApi = {
  settings: () => request<FinanceSettings>('/api/finance/settings'),
  options: () => request<FinanceOptions>('/api/admin/finance/options'),
  setSettings: (body: Partial<FinanceSettings>) => put<FinanceSettings>('/api/admin/finance/settings', body),
  cashflowItems: () => request<{ rows: CashflowItemClassRow[]; window: Window }>('/api/admin/finance/cashflow-items'),
  setCashflowItem: (guid: string, body: { cf_section: string; flow: string; pnl_line: string | null }) =>
    put<{ status: string }>(`/api/admin/finance/cashflow-items/${encodeURIComponent(guid)}`, body),
  resetCashflowItem: (guid: string) =>
    request<{ status: string }>(`/api/admin/finance/cashflow-items/${encodeURIComponent(guid)}`, { method: 'DELETE' }),
  expenseItems: () => request<{ rows: ExpenseItemClassRow[]; window: Window }>('/api/admin/finance/expense-items'),
  setExpenseItem: (guid: string, body: { pnl_line: string }) =>
    put<{ status: string }>(`/api/admin/finance/expense-items/${encodeURIComponent(guid)}`, body),
  resetExpenseItem: (guid: string) =>
    request<{ status: string }>(`/api/admin/finance/expense-items/${encodeURIComponent(guid)}`, { method: 'DELETE' }),
  counterparties: () => request<{ rows: OwnCounterpartyRow[]; coverage: DebtBranchCoverageRow[] }>('/api/admin/finance/counterparties'),
  searchCounterparties: (q: string) =>
    request<{ rows: Array<{ guid: string; name: string; inn: string | null; is_own: boolean }> }>(
      `/api/admin/finance/counterparties/search?q=${encodeURIComponent(q)}`,
    ),
  setCounterparty: (guid: string, isOwn: boolean | null) =>
    put<{ status: string }>(`/api/admin/finance/counterparties/${encodeURIComponent(guid)}`, { is_own: isOwn }),
}

export interface ExcludedDocumentRow {
  database_id: string
  doc_guid: string
  stream: string | null
  stream_title: string | null
  doc_number: string | null
  doc_date: string | null
  amount: string | null
  reason: string
  created_by: string | null
  created_at: string
}

export interface FoundDocumentRow {
  database_id: string
  doc_guid: string
  stream: string
  stream_title: string
  doc_number: string | null
  doc_date: string
  amount: string
  lines: number
  is_deleted: boolean
  excluded: boolean
}

export const exclusionsApi = {
  list: () => request<{ documents: ExcludedDocumentRow[] }>('/api/admin/excluded-documents'),
  search: (q: string) =>
    request<{ documents: FoundDocumentRow[] }>(`/api/admin/excluded-documents/search?q=${encodeURIComponent(q)}`),
  exclude: (body: { database_id: string; doc_guid: string; reason: string }) =>
    post<{ status: string; lines: number }>('/api/admin/excluded-documents', body),
  include: (databaseId: string, guid: string) =>
    request<{ status: string; note: string }>(
      `/api/admin/excluded-documents/${encodeURIComponent(databaseId)}/${encodeURIComponent(guid)}`,
      { method: 'DELETE' },
    ),
}
